import { defer, from, of, Observable, Subject, Subscription, throwError } from 'rxjs';
import { switchMap, first, catchError, tap, timeout } from 'rxjs/operators';
import { UserEventLocalService } from './user-event.local.service';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { UserEvent } from '../entities/user-events/user-event.model';
import { OfflineNotificationService } from './offline-notification.service';
import { JhiEventManager } from './event-manager.service';
import { LoggingService } from '../shared/logging.service';
import { ServerUrl } from './server-url.service';

const ATTENTE_MAX_VERROU_MS = 30_000;

/**
 * Service permettant d'envoyer les évenements utilisateur au back qu'importe la connectivité
 * * stocke les évenements en local (via LocalService), et les envoye via HTTP quand c'est possible
 */
@Injectable({
  providedIn: 'root',
})
export class UserEventWriteService implements OnDestroy {
  private readonly prefix: string = '/me/actions';

  public offlineSubscription?: Subscription;
  /**
   * Locking mechanism
   * Attention à ne s'inscrire qu'à un seul
   */
  public readonly synchroLock: Subject<any> = new Subject();
  public isSynching = false;

  constructor(
    private readonly http: HttpClient,
    private readonly localStore: UserEventLocalService,
    private readonly offlineNotificationService: OfflineNotificationService,
    private readonly eventManager: JhiEventManager,
    private readonly serverUrl: ServerUrl,
    private readonly log: LoggingService
  ) {}

  public demarrer(): void {
    this.registerOffline();
  }

  public ngOnDestroy() {
    if (this.offlineSubscription) {
      this.eventManager.destroy(this.offlineSubscription);
    }
  }

  public registerOffline() {
    this.offlineSubscription = this.eventManager.subscribe('onNetworkChange', (body: any) => {
      if (body.content.isOffline === false) {
        // si on repasse en ligne, je tente de synchroniser
        this.tryToRefresh().subscribe();
      }
    });
  }

  public tryToRefresh(): Observable<any> {
    if (!this.isSynching) {
      this.isSynching = true;
      return this.synchro();
    } else {
      return of({});
    }
  }

  public put(evt: UserEvent): Observable<any> {
    return defer(() => {
      // si une synchro est déjà lancée, on attend qu'elle soit terminée
      if (this.isSynching) {
        return this.synchroLock.pipe(
          first(),
          timeout({
            each: ATTENTE_MAX_VERROU_MS,
            with: () => {
              this.onSynchDone();
              return throwError(() => new Error(`Synchro précédente jamais terminée, événement ${evt.type} abandonné`));
            },
          }),
          switchMap(() => this.put(evt))
        );
      }
      this.isSynching = true;
      return from(this.localStore.add(evt)).pipe(switchMap(() => this.synchro()));
    });
  }

  private synchro(): Observable<any> {
    return this.tryToUpdateLocalStore().pipe(
      tap(() => this.onSynchDone()),
      catchError(err => {
        this.log.warn(
          'Synchro des évènements interrompue par une erreur inattendue : les évènements restent en tampon local ' +
            'et repartiront à la prochaine synchro. Vérifier la cause ci-dessous.',
          err
        );
        this.onSynchDone();
        return of({});
      })
    );
  }

  private onSynchDone() {
    this.isSynching = false;
    this.synchroLock.next('next');
  }

  private tryToUpdateLocalStore(): Observable<any> {
    const extracted = this.localStore.extract();
    return from(extracted).pipe(
      switchMap(evts => {
        if (evts.length > 0) {
          return this.tryToSendViaHTTP(evts).pipe(
            tap(() =>
              // on notifie qu'on est en ligne
              this.offlineNotificationService.setOnline()
            ),
            switchMap(() =>
              // on vide les evenement stockés, ils sont envoyés
              from(this.localStore.saveAll([]))
            ),
            catchError(err => {
              if (
                err.status !== undefined &&
                err.status !== 0 &&
                err.status !== 405 &&
                err.status !== 404 &&
                err.status !== 500 &&
                err.status !== 415
              ) {
                // bug serveur, pas la peine de renvoyer, on vide...
                return from(this.localStore.saveAll([]));
              } else {
                // pas joignable
                this.offlineNotificationService.setOffline();
                return of({});
              }
            })
          ); // fin pipe de events > 0
        } else {
          // si je n'ai aucun evènement, je renvoie vide
          return of({});
        }
      })
    );
  }

  private tryToSendViaHTTP(evts: UserEvent[]): Observable<HttpResponse<void>> {
    return this.http.post<void>(this.serverUrl.get() + this.prefix, evts, { observe: 'response' });
  }
}
